import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Banknote, Search, CreditCard, Landmark, HandCoins, MoreHorizontal, FileText, Wallet, Plus, Save } from "lucide-react";
import { fmtEurCompact as fmtEur } from "@/lib/formatMoney";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("de-DE") : "—";

function PaymentSectionHeading({ number, icon: Icon, title }) {
  return (
    <div className="stellar-payment-section-heading">
      <span className="stellar-payment-section-step">{number}</span>
      <Icon className="h-4 w-4" />
      <h2 className="font-display text-base font-bold">{title}</h2>
    </div>
  );
}

export const PAY_METHODS = {
  bar:           { label: "Bar",          Icon: HandCoins },
  ueberweisung:  { label: "Überweisung",  Icon: Landmark },
  karte:         { label: "Karte",        Icon: CreditCard },
  sonstige:      { label: "Sonstige",     Icon: MoreHorizontal },
};

const METHOD_FILTERS = [
  { id: "all", label: "Alle Arten", value: null },
  { id: "bar", label: "Bar", value: "bar" },
  { id: "ueberweisung", label: "Überweisung", value: "ueberweisung" },
  { id: "karte", label: "Karte", value: "karte" },
  { id: "sonstige", label: "Sonstige", value: "sonstige" },
];

const TYPE_FILTERS = [
  { id: "all",     label: "Alle",             value: null },
  { id: "invoice", label: "Mit Rechnung",     value: "invoice" },
  { id: "direct",  label: "Direktzahlungen",  value: "direct" },
];

export default function Payments() {
  const [method, setMethod] = useState(null);
  const [typeFilter, setTypeFilter] = useState(null);
  const [q, setQ] = useState("");
  const [directOpen, setDirectOpen] = useState(false);
  const qc = useQueryClient();

  const { data = [] } = useQuery({
    queryKey: ["payments", method, typeFilter],
    queryFn: async () => (await api.get("/payments", { params: {
      method: method || undefined,
      type: typeFilter || undefined,
    } })).data,
  });

  const filtered = q
    ? data.filter((p) => {
        const s = q.toLowerCase();
        return (p.invoice_number || "").toLowerCase().includes(s)
          || (p.customer_name || "").toLowerCase().includes(s)
          || (p.reference || "").toLowerCase().includes(s);
      })
    : data;

  const total = filtered.reduce((a, p) => a + (p.amount || 0), 0);
  const invTotal = filtered.filter(p => p.type !== "direct").reduce((a, p) => a + (p.amount || 0), 0);
  const dirTotal = filtered.filter(p => p.type === "direct").reduce((a, p) => a + (p.amount || 0), 0);

  return (
    <div className="stellar-page">
      <div className="stellar-page-heading stellar-heading-teal flex flex-wrap items-center gap-3 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <div className="stellar-module-icon stellar-module-teal"><Banknote /></div>
            <h1 className="font-display text-2xl sm:text-4xl font-bold tracking-tight">Zahlungen</h1>
          </div>
        </div>
      </div>

      {/* Summary chips */}
      <section className="stellar-payment-section">
      <PaymentSectionHeading number="1" icon={Banknote} title="Zahlungsübersicht" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
        <div className="premium-card stellar-summary-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><FileText className="h-3 w-3" /> Rechnungszahlungen</div>
          <div className="font-display text-lg font-semibold mt-1" data-testid="summary-invoice-payments">{fmtEur(invTotal)}</div>
        </div>
        <div className="premium-card stellar-summary-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Wallet className="h-3 w-3" /> Direktzahlungen</div>
          <div className="font-display text-lg font-semibold mt-1" data-testid="summary-direct-payments">{fmtEur(dirTotal)}</div>
        </div>
        <div className="premium-card stellar-summary-card stellar-summary-total p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Banknote className="h-3 w-3" /> Gesamt</div>
          <div className="font-display text-lg font-semibold mt-1" data-testid="summary-total-payments">{fmtEur(total)}</div>
        </div>
      </div>
      </section>

      {/* Type + method filters */}
      <section className="stellar-payment-section">
      <PaymentSectionHeading number="2" icon={FileText} title="Zahlungsliste" />
      <div className="premium-card overflow-hidden">
      <div className="stellar-payment-toolbar p-4 border-b border-border/60">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Rechnung / Kunde / Beleg…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 w-full bg-white" data-testid="payments-search" />
        </div>
        <Button onClick={() => setDirectOpen(true)} data-testid="new-direct-payment-btn" className="rounded-xl bg-teal-600 hover:bg-teal-700">
          <Wallet className="h-4 w-4 mr-1" /> Direktzahlung erfassen
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 p-4 border-b border-border/60 bg-slate-50/50">
        <div className="stellar-segmented">
          {TYPE_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setTypeFilter(f.value)}
              data-testid={`payment-type-${f.id}`}
              className={`px-3 py-1.5 text-sm rounded-lg whitespace-nowrap ${typeFilter === f.value ? "bg-white text-teal-700 shadow-sm font-semibold" : "text-muted-foreground"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="stellar-segmented">
          {METHOD_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setMethod(f.value)}
              data-testid={`payment-filter-${f.id}`}
              className={`px-3 py-1.5 text-sm rounded-lg whitespace-nowrap ${method === f.value ? "bg-white text-teal-700 shadow-sm font-semibold" : "text-muted-foreground"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="ml-auto text-sm text-muted-foreground">{filtered.length} Zahlungen</div>
      </div>

      <div>
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-sm text-muted-foreground">
            <Banknote className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <div className="mb-1">Keine Zahlungen.</div>
            <div className="text-xs">Erfassen Sie eine Rechnungszahlung aus einer Rechnung oder eine Direktzahlung oben rechts.</div>
          </div>
        ) : (
          <>
          <div className="sm:hidden divide-y divide-border/60" data-testid="payments-mobile-list">
            {filtered.map((p) => {
              const m = PAY_METHODS[p.payment_method] || PAY_METHODS.sonstige;
              const isDirect = p.type === "direct";
              return (
                <article key={p.id} className="p-4" data-testid={`payment-mobile-card-${p.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs text-muted-foreground">{fmtDate(p.payment_date)}</div>
                      <div className="mt-1 truncate font-display font-semibold">
                        {p.customer_id ? (
                          <Link to={`/kunden/${p.customer_id}`} className="hover:underline">{p.customer_name || "Ohne Kunde"}</Link>
                        ) : (p.customer_name || "Ohne Kunde")}
                      </div>
                    </div>
                    <div className="shrink-0 text-right font-display text-lg font-bold text-teal-700">{fmtEur(p.amount)}</div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-medium ${
                      isDirect ? "bg-purple-500/10 text-purple-700 dark:text-purple-400" : "bg-blue-500/10 text-blue-700 dark:text-blue-400"
                    }`}>
                      {isDirect ? <><Wallet className="h-3 w-3" /> Direktzahlung</> : <><FileText className="h-3 w-3" /> Rechnung</>}
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-[10px] text-slate-600">
                      <m.Icon className="h-3 w-3" /> {m.label}
                    </span>
                  </div>
                  {(p.invoice_id || p.reference) && (
                    <div className="mt-3 flex flex-col gap-1 border-t border-border/50 pt-3 text-xs text-muted-foreground">
                      {p.invoice_id && (
                        <div className="flex items-center justify-between gap-3">
                          <span>Rechnung</span>
                          <Link to={`/rechnungen/${p.invoice_id}`} className="truncate font-mono font-medium text-foreground hover:underline">{p.invoice_number || "—"}</Link>
                        </div>
                      )}
                      {p.reference && (
                        <div className="flex items-start justify-between gap-3">
                          <span>Beleg</span>
                          <span className="max-w-[65%] break-words text-right text-foreground">{p.reference}</span>
                        </div>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
          <div className="hidden sm:block overflow-x-auto">
          <table className="stellar-data-table w-full min-w-[760px] text-sm">
            <thead className="bg-slate-50/80 text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Datum</th>
                <th className="text-left px-4 py-3">Typ</th>
                <th className="text-left px-4 py-3">Rechnung</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Kunde</th>
                <th className="text-left px-4 py-3">Zahlungsart</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">Beleg</th>
                <th className="text-right px-4 py-3">Betrag</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {filtered.map((p) => {
                const m = PAY_METHODS[p.payment_method] || PAY_METHODS.sonstige;
                const isDirect = p.type === "direct";
                return (
                  <tr key={p.id} className="stellar-table-row" data-testid={`payment-row-${p.id}`}>
                    <td className="px-4 py-3 text-muted-foreground">{fmtDate(p.payment_date)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                        isDirect ? "bg-purple-500/10 text-purple-700 dark:text-purple-400" : "bg-blue-500/10 text-blue-700 dark:text-blue-400"
                      }`} data-testid={`payment-type-badge-${p.id}`}>
                        {isDirect ? <><Wallet className="h-3 w-3" /> Direkt</> : <><FileText className="h-3 w-3" /> Rechnung</>}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {p.invoice_id ? (
                        <Link to={`/rechnungen/${p.invoice_id}`} className="font-mono font-medium hover:underline">{p.invoice_number || "—"}</Link>
                      ) : <span className="text-muted-foreground text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">
                      {p.customer_id ? (
                        <Link to={`/kunden/${p.customer_id}`} className="hover:underline">{p.customer_name || "—"}</Link>
                      ) : (p.customer_name || "—")}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                        <m.Icon className="h-3.5 w-3.5" /> {m.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground hidden lg:table-cell">{p.reference || "—"}</td>
                    <td className="px-4 py-3 text-right font-medium">{fmtEur(p.amount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          </>
        )}
      </div>
      </div>
      </section>

      <DirectPaymentDialog
        open={directOpen}
        onOpenChange={setDirectOpen}
        onSuccess={() => {
          qc.invalidateQueries({ queryKey: ["payments"] });
          qc.invalidateQueries({ queryKey: ["dashboard"] });
        }}
      />
    </div>
  );
}

export function DirectPaymentDialog({ open, onOpenChange, onSuccess, defaultCustomerId = "", defaultCustomerName = "", defaultAmount = 0, defaultJobId = null, defaultVehicleId = null }) {
  const [amount, setAmount] = useState(defaultAmount);
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState("bar");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [customerId, setCustomerId] = useState(defaultCustomerId);
  const [customerSearch, setCustomerSearch] = useState(defaultCustomerName);

  const { data: customers = [] } = useQuery({
    queryKey: ["customers", customerSearch],
    queryFn: async () => (await api.get(`/customers?q=${encodeURIComponent(customerSearch)}`)).data,
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setAmount(defaultAmount || 0);
      setPayDate(new Date().toISOString().slice(0, 10));
      setMethod("bar"); setReference(""); setNotes("");
      setCustomerId(defaultCustomerId || "");
      setCustomerSearch(defaultCustomerName || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submitMut = useMutation({
    mutationFn: async () => (await api.post(`/payments/direct`, {
      amount: Number(amount),
      payment_date: payDate,
      payment_method: method,
      reference,
      notes,
      customer_id: customerId || null,
      vehicle_id: defaultVehicleId || null,
      job_id: defaultJobId || null,
    })).data,
    onSuccess: () => { toast.success("Direktzahlung erfasst"); onSuccess?.(); onOpenChange(false); },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="direct-payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5" /> Direktzahlung erfassen
          </DialogTitle>
        </DialogHeader>
        <div className="rounded-md border border-purple-500/30 bg-purple-500/5 p-3 text-xs text-purple-700 dark:text-purple-400">
          Diese Zahlung wird nicht mit einer Rechnung verknüpft. Ein Kunde kann optional zugeordnet werden.
        </div>
        <div className="space-y-3">
          <div>
            <Label>Betrag (€)</Label>
            <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="direct-payment-amount" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Datum</Label>
              <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} data-testid="direct-payment-date" />
            </div>
            <div>
              <Label>Zahlungsart</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="direct-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bar">Bar</SelectItem>
                  <SelectItem value="ueberweisung">Überweisung</SelectItem>
                  <SelectItem value="karte">Karte</SelectItem>
                  <SelectItem value="sonstige">Sonstige</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Kunde (optional)</Label>
            <Input placeholder="Kunde suchen…" value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)} data-testid="direct-payment-customer-search" />
            {customerSearch.length >= 2 && customers.length > 0 && !customerId && (
              <div className="mt-1 max-h-32 overflow-auto rounded-md border bg-popover">
                {customers.slice(0, 6).map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => { setCustomerId(c.id); setCustomerSearch(c.name); }}
                    data-testid={`direct-payment-customer-${c.id}`}
                    className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted"
                  >
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-muted-foreground">{c.phone}</div>
                  </button>
                ))}
              </div>
            )}
            {customerId && (
              <button type="button" onClick={() => { setCustomerId(""); setCustomerSearch(""); }} className="mt-1 text-xs text-muted-foreground hover:text-foreground">
                Kunde entfernen
              </button>
            )}
          </div>
          <div>
            <Label>Beleg / Referenz</Label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="z.B. Quittung Nr." data-testid="direct-payment-reference" />
          </div>
          <div>
            <Label>Anmerkungen</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="direct-payment-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={() => submitMut.mutate()} disabled={submitMut.isPending || !amount || Number(amount) <= 0} data-testid="direct-payment-submit">
            <Save className="h-4 w-4 mr-1" /> Erfassen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
